import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useWeekPlan } from "@/context/week-plan-context";
import { Button } from "@/components/ui/button";
import {
  formatMonthYear,
  getWeeksInMonth,
  getWeekDates,
  getCurrentWeekId,
  getTodayDayOfWeek,
} from "@/lib/week-utils";
import type { WeekSummary } from "@/lib/api";
import type { DayStatus } from "@/lib/plan-status";

const DAY_HEADERS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export function CalendarPage() {
  const { state } = useWeekPlan();
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const currentWeekId = getCurrentWeekId();

  const weekSummaryMap = useMemo(
    () => new Map((state.weekList ?? []).map((w) => [w.weekId, w])),
    [state.weekList],
  );

  const weeks = useMemo(() => getWeeksInMonth(year, month), [year, month]);

  function prevMonth() {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  }

  function nextMonth() {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  }

  function handleWeekClick(weekId: string) {
    const todayDay = getTodayDayOfWeek(weekId);
    navigate(`/week/${weekId}/${todayDay ?? "mon"}`);
  }

  return (
    <div className="w-full mx-auto max-w-3xl px-4 py-3 flex-1 overflow-y-auto">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={prevMonth}>
            &larr;
          </Button>
          <h2 className="text-lg font-semibold">{formatMonthYear(year, month)}</h2>
          <Button variant="ghost" size="sm" onClick={nextMonth}>
            &rarr;
          </Button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-px text-center">
          {DAY_HEADERS.map((d) => (
            <div key={d} className="text-xs font-medium text-muted-foreground py-1">
              {d}
            </div>
          ))}
        </div>

        {/* Week rows */}
        <div className="space-y-1">
          {weeks.map((weekId) => {
            const days = getWeekDates(weekId);
            const summary = weekSummaryMap.get(weekId);
            const weekStatus = summary?.status ?? "not_planned";
            const isCurrent = weekId === currentWeekId;

            return (
              <button
                key={weekId}
                onClick={() => handleWeekClick(weekId)}
                className={`grid grid-cols-7 gap-px w-full rounded-lg p-1 transition-colors ${
                  isCurrent
                    ? "bg-primary/10 hover:bg-primary/15 ring-1 ring-primary/30"
                    : weekStatus === "planned"
                      ? "bg-green-50 hover:bg-green-100"
                      : weekStatus === "partial"
                        ? "bg-amber-50 hover:bg-amber-100"
                        : "bg-muted/30 hover:bg-muted/60"
                }`}
              >
                {days.map(({ day, date, dayNum }) => {
                  const isInMonth =
                    new Date(date + "T00:00:00").getMonth() === month;
                  const isToday = date === formatToday();
                  const dayStatus: DayStatus = summary?.dayStatuses[day]?.status ?? "not_planned";

                  return (
                    <div
                      key={date}
                      className={`text-center py-2 text-sm rounded relative ${
                        isToday
                          ? "bg-primary text-primary-foreground font-bold"
                          : isInMonth
                            ? "text-foreground"
                            : "text-muted-foreground/40"
                      }`}
                    >
                      {dayNum}
                      {summary && dayStatus !== "not_planned" && (
                        <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 size-1 rounded-full ${
                          dayStatus === "planned"
                            ? "bg-green-500"
                            : "bg-amber-500"
                        }`} />
                      )}
                    </div>
                  );
                })}
                {/* Week status indicator */}
                <div className="col-span-7 flex items-center justify-center pb-1">
                  {weekStatus === "planned" ? (
                    <span className="text-[10px] text-green-600 font-medium">
                      Planificada
                    </span>
                  ) : weekStatus === "partial" ? (
                    <span className="text-[10px] text-amber-600 font-medium">
                      {summary!.plannedDayCount}/7 días planificados
                    </span>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">
                      Click para planificar
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function formatToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
